import {
  AgreementState,
  PraksisstedForAvtale,
  useAgreementContext,
} from './index';
import { associateBy } from '../../util';
import { MinimalSted } from './stedsections';
import { parseIsoTzLess } from '../../date.utils';
import { getDatoInstance, praksistyperToKoder } from './agreementutils';

const useChangesToPeriodCouldAffectAktiviteter = (
  agreementInitial: AgreementState | undefined,
): boolean => {
  const { startdato, sluttdato } = useAgreementContext();

  if (!agreementInitial) return false;

  const { startdato: startdatoInitial, sluttdato: sluttdatoInitial } =
    agreementInitial;

  const startdatoInstance = getDatoInstance(startdato);
  const sluttdatoInstance = getDatoInstance(sluttdato);

  return (
    (!!startdatoInstance &&
      startdatoInitial !== startdato &&
      parseIsoTzLess(startdatoInitial!) < startdatoInstance) ||
    (!!sluttdatoInstance &&
      (!sluttdatoInitial ||
        (sluttdatoInitial !== sluttdato &&
          parseIsoTzLess(sluttdatoInitial) > sluttdatoInstance)))
  );
};

const useChangesToPraksistyperCouldAffectAktiviteter = (
  agreementInitial: AgreementState | undefined,
): boolean => {
  const { praksistyper } = useAgreementContext();

  if (!agreementInitial) return false;

  const { praksistyper: praksistyperInitial } = agreementInitial;

  const praksistypekoder = praksistyperToKoder(praksistyper);
  const praksistypekoderInitial = praksistyperToKoder(praksistyperInitial);

  return (
    praksistypekoderInitial.length > praksistypekoder.length ||
    !praksistypekoderInitial.every((kode) => praksistypekoder.includes(kode))
  );
};

const getIdAndUndernodeIds = (sted: MinimalSted): string[] =>
  (sted.noder.under as MinimalSted[]).reduce(
    (acc: string[], undernode: MinimalSted) =>
      acc.concat(getIdAndUndernodeIds(undernode)),
    [sted.id],
  );

const getAccUndernodeIds = (sted: MinimalSted) =>
  sted.noder.under.flatMap((undernode) => getIdAndUndernodeIds(undernode));

const changesToPraksisstedCouldAffectAktiviteter = (
  praksisstedInital: PraksisstedForAvtale,
  praksissted: PraksisstedForAvtale,
) => {
  const { id: idInital, checkedIds: checkedIdsInitial } = praksisstedInital;
  const { id, checkedIds } = praksissted;

  return (
    idInital !== id ||
    (checkedIds.length > 0 &&
      (checkedIdsInitial.length > checkedIds.length ||
        !checkedIdsInitial.every((idInitial) =>
          checkedIds.includes(idInitial),
        ) ||
        (checkedIdsInitial.length === 0 &&
          checkedIds.length < getAccUndernodeIds(praksissted).length)))
  );
};

type PraksisstederByIdType = {
  [praksisstedId: string]: PraksisstedForAvtale;
};

const useChangesToPraksisstederCouldAffectAktiviteter = (
  agreementInitial: AgreementState | undefined,
): boolean => {
  const { praksissteder } = useAgreementContext();

  if (!agreementInitial) return false;

  const { praksissteder: praksisstederInitial } = agreementInitial;

  if (!praksisstederInitial) {
    return false;
  }
  if (!praksissteder || praksisstederInitial.length > praksissteder.length) {
    return true;
  }

  const praksisstederById: PraksisstederByIdType = associateBy(
    praksissteder,
    (praksissted) => praksissted.id,
  );

  return !praksisstederInitial.every((praksisstedInitial) => {
    const praksissted = praksisstederById[praksisstedInitial.id];

    return (
      !!praksissted &&
      !changesToPraksisstedCouldAffectAktiviteter(
        praksisstedInitial,
        praksissted,
      )
    );
  });
};

const useChangesCouldAffectAktiviteter = (
  agreementInitial: AgreementState | undefined,
): boolean => {
  const changesToPeriodCouldAffectAktiviteter =
    useChangesToPeriodCouldAffectAktiviteter(agreementInitial);
  const changesToPraksistyperCouldAffectAktiviteter =
    useChangesToPraksistyperCouldAffectAktiviteter(agreementInitial);
  const changesToPraksisstederCouldAffectAktiviteter =
    useChangesToPraksisstederCouldAffectAktiviteter(agreementInitial);

  return (
    changesToPeriodCouldAffectAktiviteter ||
    changesToPraksistyperCouldAffectAktiviteter ||
    changesToPraksisstederCouldAffectAktiviteter
  );
};

export default useChangesCouldAffectAktiviteter;
